import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { pb } from "@/lib/pocketbase";

interface Translation {
  translations: {
    np: {
      name: string;
      description: string;
      viewMore: string;
      about: string;
    };
  };
}

interface Cooperative {
  id: string;
  image: string;
  name: string;
  slug: string; 
}

interface AboutProps {
  product: {
    id: string;  // Add this
    name: string;
    description: string;
    cooperative: string;
    expand?: {
      cooperative: {
        id: string;
        image: string;
        name: string;
        slug: string;
      };
    };
  };
}

export const About: React.FC<AboutProps> = ({ product }) => {
  const [searchParams] = useSearchParams();
  const [translations, setTranslations] = useState<Translation | null>(null);
  const locale = searchParams.get('locale') || 'en';

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const translation = await pb.collection('translations').getFirstListItem<Translation>(
          `collection_name = "products" && record_id = "${product.id}"`
        );
        
        if (translation?.translations?.np) {
          setTranslations(translation);
        }
      } catch (error) {
        // Silently handle error - component will use fallback content
      }
    };

    if (product.id) {
      loadTranslations();
    }
  }, [product.id]);

  const getTranslatedContent = (field: keyof Translation['translations']['np']) => {
    // For English locale, return default content
    if (locale === 'en') {
      switch (field) {
        case 'name': return product.name;
        case 'description': return product.description;
        case 'viewMore': return 'View More';
        case 'about': return 'About';
        default: return '';
      }
    }
    
    // For Nepali locale, return translation or fallback to English
    const npContent = translations?.translations?.np?.[field];
    if (npContent) return npContent;

    // Fallback to English content without recursive call
    switch (field) {
      case 'name': return product.name;
      case 'description': return product.description;
      case 'viewMore': return 'View More';
      case 'about': return 'About';
      default: return '';
    }
  };

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  return (
    <section className="w-full mt-12 mb-32">
      <div className="relative">
        <img
          src={pb.files.getURL(product.expand?.cooperative!, product.expand?.cooperative?.image!)}
          loading='lazy'
          alt={`${product.expand?.cooperative?.name} background`}
          className="w-full object-cover aspect-video"
        />
        <div className="absolute bottom-0 left-0 right-0 flex justify-center px-4 m">
          <div className="bg-[rgba(214,255,235,1)] rounded-lg p-6 max-w-[320px] w-full shadow-lg translate-y-1/2">
            <h2 className={`text-[rgba(0,63,40,1)] text-2xl font-bold mb-2 ${getFontClass()}`}>
              {getTranslatedContent('about')} {getTranslatedContent('name')}
            </h2>
            <p className={`text-[rgba(0,63,40,1)] text-lg leading-relaxed ${getFontClass()}`}>
              {getTranslatedContent('description')}
            </p>
            <Link 
              to={`/sahakari/${product.expand?.cooperative?.slug}`}
              className={`mt-8 px-6 py-2 bg-[rgba(0,63,40,1)] text-white rounded-full text-base hover:bg-[rgba(0,63,40,0.9)] transition-colors inline-block ${getFontClass()}`}
            >
              {getTranslatedContent('viewMore')}
            </Link>
          </div>
        </div>
      </div>
      <div className="h-[120px]"></div>
    </section>
  );
};